import { GranularPermissions } from '@/types/permissions';

/**
 * Check if user has a specific permission
 * @param permissions - User's permission object
 * @param path - Dot-notation permission path (e.g., 'programs.published.create')
 * @param adminType - Optional admin type ('ADMIN' auto-passes all checks)
 * @returns true if permission granted, false otherwise
 */
export const can = (
  permissions: GranularPermissions | null,
  path: string,
  adminType?: string | null
): boolean => {
  if (adminType === 'ADMIN') return true;
  if (!permissions) return false;

  const parts = path.split('.');
  let current: any = permissions;
  
  for (const part of parts) {
    if (current?.[part] === undefined) return false;
    current = current[part];
  }
  
  return current === true;
};

/**
 * Check if user has ANY of the specified permissions (OR logic)
 * @param permissions - User's permission object
 * @param paths - Array of permission paths to check
 * @param adminType - Optional admin type
 * @returns true if user has at least one of the permissions
 */
export const canAny = (
  permissions: GranularPermissions | null,
  paths: string[],
  adminType?: string | null
): boolean => {
  if (adminType === 'ADMIN') return true;
  return paths.some(path => can(permissions, path, adminType));
};

/**
 * Check if user has ALL of the specified permissions (AND logic)
 * @param permissions - User's permission object
 * @param paths - Array of permission paths to check
 * @param adminType - Optional admin type
 * @returns true if user has all of the permissions
 */
export const canAll = (
  permissions: GranularPermissions | null,
  paths: string[],
  adminType?: string | null
): boolean => {
  if (adminType === 'ADMIN') return true;
  return paths.every(path => can(permissions, path, adminType));
};

// Alias for backward compatibility
export const hasPermission = can;

export const isAdmin = (adminType: string) => adminType === 'ADMIN';
